import { Box, Flex, Grid, GridItem, Heading, Text } from '@chakra-ui/react';
import { branchRegionsApi } from '../../../api/endpoints/branchRegionsApi';
import { templateRangeTiersApi } from '../../../api/endpoints/metadata/templateRangeTiersApi';
import {
  compositionGroupTemplatePriceApi,
  CompositionGroupTemplatePrice,
} from '../../../api/endpoints/compositionGroupTemplatePriceApi';
import { useApiResource } from '../../../api/hooks/useApiResource';
import React, { useEffect, useMemo } from 'react';
import { TemplatePriceCell } from './TemplatePriceCell';
import { SimpleNote } from '@texas/components/shared/SimpleNote';
import { AngularNavLink } from '@texas/components/shared/AngularNavLink';
import { useTranslation } from 'react-i18next';

interface TemplatePricingProps {
  compositionGroupPurchaseInfoId: number;
}

export function TemplatePricing({
  compositionGroupPurchaseInfoId,
}: TemplatePricingProps) {
  const { t } = useTranslation();
  const { data: branchRegions, refetch: refetchBranchRegions } = useApiResource(
    branchRegionsApi.getAll,
  );
  const { data: templateRangeTiers, refetch: refetchTemplateRangeTiers } =
    useApiResource(templateRangeTiersApi.getAll);
  const { data: templatePrices, refetch: refetchTemplatePrices } =
    useApiResource(compositionGroupTemplatePriceApi.getAll);

  useEffect(() => {
    refetchBranchRegions('');
    refetchTemplateRangeTiers({
      searchTerm: '',
      includeArchived: false,
    });
    refetchTemplatePrices(compositionGroupPurchaseInfoId);
  }, [
    refetchBranchRegions,
    refetchTemplateRangeTiers,
    refetchTemplatePrices,
    compositionGroupPurchaseInfoId,
  ]);

  // Create a lookup map for quick price cell retrieval
  const priceMap = useMemo(() => {
    const map = new Map<string, CompositionGroupTemplatePrice>();
    templatePrices?.forEach((price) => {
      const key = `${price.branchRegionId}-${price.templateRangeTierId}`;
      map.set(key, price);
    });
    return map;
  }, [templatePrices]);

  return (
    <Box>
      <Grid
        templateColumns={`auto repeat(${branchRegions?.length}, 250px)`}
        gap={2}
        alignItems="center"
      >
        <GridItem />

        {branchRegions?.map((region) => (
          <GridItem key={region.id}>
            <Box textAlign="center" mb={2}>
              <Heading size="md" mb={1}>
                {region.name}
              </Heading>
              <Flex gap={2} justifyContent="center">
                {region.branches.map((branch) => (
                  <Text variant="sub" key={branch.id}>
                    {branch.erp}
                  </Text>
                ))}
              </Flex>
            </Box>
          </GridItem>
        ))}

        {templateRangeTiers?.map((tier) => (
          <React.Fragment key={tier.id}>
            <GridItem textAlign="end">
              <Text fontSize="sm" fontWeight="medium">
                {tier.startRange.toLocaleString()} -{' '}
                {tier.endRange.toLocaleString()}
              </Text>
            </GridItem>

            {branchRegions?.map((region) => {
              const key = `${region.id}-${tier.id}`;
              const existingPrice = priceMap.get(key);
              return (
                <GridItem key={`cell-${tier.id}-${region.id}`}>
                  <TemplatePriceCell
                    branchRegion={region}
                    templateRangeTier={tier}
                    compositionGroupPurchaseInfoId={
                      compositionGroupPurchaseInfoId
                    }
                    existingPrice={existingPrice}
                  />
                </GridItem>
              );
            })}
          </React.Fragment>
        ))}
      </Grid>
      {branchRegions?.length === 0 && (
        <SimpleNote pt={12}>
          <AngularNavLink to="configuration.branchRegions">
            {t('configuration.configureBranchRegions')}
          </AngularNavLink>
        </SimpleNote>
      )}
      {templateRangeTiers?.length === 0 && (
        <SimpleNote pt={12}>
          <AngularNavLink to="configuration.vdcTiers">
            {t('configuration.configureVdcTiers')}
          </AngularNavLink>
        </SimpleNote>
      )}
    </Box>
  );
}
